import { useState } from 'react';
import {
  Button,
  IconButton,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { apiDownloadMvrRequestPdf } from 'api';
import type { MVRRequestApiError } from 'api';

interface MvrDownloadButtonProps {
  mvrOrderId: number;
  mvrRequestId: number;
  licenseNumber: string;
  iconOnly?: boolean;
}

const MvrDownloadButton = ({
  mvrOrderId,
  mvrRequestId,
  licenseNumber,
  iconOnly = false,
}: MvrDownloadButtonProps) => {
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDownloading(true);

    try {
      await apiDownloadMvrRequestPdf(mvrOrderId, mvrRequestId, licenseNumber);
    } catch (error: any) {
      const apiError = error as MVRRequestApiError;
      toast({
        title: 'Error Downloading MVR PDF',
        description: apiError.data?.error || 'Failed to download MVR PDF',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (iconOnly) {
    return (
      <Tooltip label="Download">
        <IconButton
          aria-label="Download"
          icon={isDownloading ? <Spinner size="xs" /> : <DownloadIcon />}
          size="xs"
          variant="solid"
          colorScheme="blue"
          isDisabled={isDownloading}
          onClick={handleDownload}
        />
      </Tooltip>
    );
  }

  return (
    <Button
      leftIcon={isDownloading ? <Spinner size="xs" /> : <DownloadIcon />}
      size="sm"
      variant="solid"
      colorScheme="blue"
      isDisabled={isDownloading}
      onClick={handleDownload}
    >
      Download
    </Button>
  );
};

export default MvrDownloadButton;
